// Offline Data Manager
// Handles offline storage, data synchronization, and offline functionality

import { openDB } from 'idb';
import logger from './logger';

const DB_NAME = 'SpeakRootsOffline';
const DB_VERSION = 2; // Incremented for priority field addition

// Database stores
const STORES = {
  PROGRESS: 'progress',
  LESSONS: 'lessons',
  ACTIVITIES: 'activities',
  AUDIO: 'audio',
  ANALYTICS: 'analytics',
  SYNC_QUEUE: 'syncQueue',
  DEAD_LETTER: 'deadLetterQueue' // For permanently failed items
};

// 🛡️ SYNC QUEUE CONFIGURATION
const SYNC_CONFIG = {
  MAX_QUEUE_SIZE: 1000, // Maximum queue items
  MAX_RETRIES: 5,
  MAX_DEAD_LETTER_SIZE: 100, // Keep last 100 failed items for debugging
  
  // Priority levels
  PRIORITY: {
    CRITICAL: 1,  // Subscription changes, payments, lesson completions
    HIGH: 2,      // Progress updates, streak updates
    MEDIUM: 3,    // Profile updates, settings changes
    LOW: 4        // Analytics, non-critical events
  },
  
  // Backoff configuration
  BACKOFF: {
    INITIAL: 1000,    // 1 second
    MAX: 60000,       // 1 minute
    MULTIPLIER: 2
  }
};

class OfflineManager {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.dbHealthy = true;
    this.lastHealthCheck = null;
    
    this.init();
  }
  
  async init() {
    await this.openDatabase();
    this.setupOnlineOfflineHandlers();
    this.startPeriodicSync();
    this.startHealthChecks(); // Start periodic health monitoring
  }
  
  // 🛡️ CORRUPTION DETECTION & RECOVERY
  async openDatabase() {
    try {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion, transaction) {
          // Progress store
          if (!db.objectStoreNames.contains(STORES.PROGRESS)) {
            db.createObjectStore(STORES.PROGRESS, { keyPath: 'id' });
          }
          
          // Lessons store
          if (!db.objectStoreNames.contains(STORES.LESSONS)) {
            db.createObjectStore(STORES.LESSONS, { keyPath: 'id' });
          }
          
          // Activities store
          if (!db.objectStoreNames.contains(STORES.ACTIVITIES)) {
            db.createObjectStore(STORES.ACTIVITIES, { keyPath: 'id' });
          }
          
          // Audio store
          if (!db.objectStoreNames.contains(STORES.AUDIO)) {
            db.createObjectStore(STORES.AUDIO, { keyPath: 'url' });
          }
          
          // Analytics store
          if (!db.objectStoreNames.contains(STORES.ANALYTICS)) {
            db.createObjectStore(STORES.ANALYTICS, { keyPath: 'id', autoIncrement: true });
          }
          
          // Sync queue store with priority index
          if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
            const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
            // Create index for priority-based queries
            syncStore.createIndex('priority', 'priority', { unique: false });
            syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          } else if (oldVersion < 2) {
            // Upgrade existing store to add priority index
            const syncStore = transaction.objectStore(STORES.SYNC_QUEUE);
            if (!syncStore.indexNames.contains('priority')) {
              syncStore.createIndex('priority', 'priority', { unique: false });
            }
            if (!syncStore.indexNames.contains('timestamp')) {
              syncStore.createIndex('timestamp', 'timestamp', { unique: false });
            }
          }
          
          // Dead letter queue for failed items
          if (!db.objectStoreNames.contains(STORES.DEAD_LETTER)) {
            const deadLetterStore = db.createObjectStore(STORES.DEAD_LETTER, { keyPath: 'id', autoIncrement: true });
            deadLetterStore.createIndex('timestamp', 'timestamp', { unique: false });
          }
        }
      });
      
      logger.info('OfflineManager', 'Info');
      
      // Run initial health check
      await this.runHealthCheck();
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      
      // Detect corruption errors
      if (this.isCorruptionError(error)) {
        logger.warn('OfflineManager', 'Warning');
        await this.recoverFromCorruption();
      }
    }
  }
  
  // 🛡️ DETECT CORRUPTION ERRORS
  isCorruptionError(error) {
    const corruptionIndicators = [
      'data is corrupted',
      'unable to open',
      'version change transaction',
      'quota',
      'ConstraintError',
      'Internal error opening backing store',
      'The operation failed because the requested database object'
    ];
    
    const errorString = error.toString().toLowerCase();
    return corruptionIndicators.some(indicator => 
      errorString.includes(indicator.toLowerCase())
    );
  }
  
  // 🛡️ RUN HEALTH CHECK
  async runHealthCheck() {
    if (!this.db) {
      this.dbHealthy = false;
      return false;
    }
    
    try {
      logger.info('OfflineManager', 'Info');
      
      // Test 1: Can we read from all stores?
      for (const storeName of Object.values(STORES)) {
        try {
          const tx = this.db.transaction(storeName, 'readonly');
          const store = tx.objectStore(storeName);
          await store.count(); // Test read operation
        } catch (error) {
          logger.error('OfflineManager', 'Error', error);
          this.dbHealthy = false;
          
          if (this.isCorruptionError(error)) {
            await this.recoverFromCorruption();
            return false;
          }
        }
      }
      
      // Test 2: Can we write to a store?
      try {
        const testData = {
          id: 'health_check_' + Date.now(),
          timestamp: Date.now(),
          test: true
        };
        await this.db.put(STORES.ANALYTICS, testData);
        await this.db.delete(STORES.ANALYTICS, testData.id);
      } catch (error) {
        logger.error('OfflineManager', 'Error', error);
        this.dbHealthy = false;
        
        // Check if it's a quota error
        if (error.name === 'QuotaExceededError') {
          logger.warn('OfflineManager', 'Warning');
          await this.cleanupOldData();
        } else if (this.isCorruptionError(error)) {
          await this.recoverFromCorruption();
          return false;
        }
      }
      
      // Test 3: Validate data integrity in sync queue
      try {
        const syncItems = await this.db.getAll(STORES.SYNC_QUEUE);
        let corruptedItems = 0;
        
        for (const item of syncItems) {
          if (!item.type || !item.data || !item.timestamp) {
            corruptedItems++;
            logger.warn('OfflineManager', 'Warning', item.id);
            // Remove corrupted item
            await this.db.delete(STORES.SYNC_QUEUE, item.id);
          }
        }
        
        if (corruptedItems > 0) {
          logger.warn('OfflineManager', 'Warning');
        }
      } catch (error) {
        logger.error('OfflineManager', 'Error', error);
      }
      
      this.dbHealthy = true;
      this.lastHealthCheck = Date.now();
      logger.info('OfflineManager', 'Info');
      return true;
      
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      this.dbHealthy = false;
      return false;
    }
  }
  
  // 🛡️ START PERIODIC HEALTH CHECKS
  startHealthChecks() {
    // Run health check every 30 minutes
    setInterval(async () => {
      logger.info('OfflineManager', 'Info');
      await this.runHealthCheck();
    }, 30 * 60 * 1000);
  }
  
  // 🛡️ RECOVER FROM CORRUPTION
  async recoverFromCorruption() {
    logger.warn('OfflineManager', 'Warning');
    
    try {
      // Step 1: Close existing database
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      
      // Step 2: Delete corrupted database
      await this.deleteDatabase();
      
      // Step 3: Notify user (could be a callback or event)
      logger.warn('OfflineManager', 'Warning');
      logger.warn('OfflineManager', 'Warning');
      
      // Emit event for UI notification
      window.dispatchEvent(new CustomEvent('indexeddb-recovered', {
        detail: { message: 'Local data was corrupted and reset. Your progress is safe in the cloud.' }
      }));
      
      // Step 4: Recreate database
      await this.openDatabase();
      
      // Step 5: Trigger sync to restore data from server
      if (this.isOnline) {
        logger.info('OfflineManager', 'Info');
        // The app should re-fetch data from Firestore
        window.dispatchEvent(new CustomEvent('restore-from-server'));
      }
      
      logger.info('OfflineManager', 'Info');
      return true;
      
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      
      // Last resort: Alert user and reload page
      alert('Unable to recover local storage. The page will reload to restore your data.');
      window.location.reload();
      return false;
    }
  }
  
  // 🛡️ DELETE DATABASE
  async deleteDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DB_NAME);
      
      request.onsuccess = () => {
        logger.info('OfflineManager', 'Info');
        resolve();
      };
      
      request.onerror = () => {
        logger.error('OfflineManager', 'Error');
        reject(request.error);
      };
      
      request.onblocked = () => {
        logger.warn('OfflineManager', 'Warning');
        // Try again after a delay
        setTimeout(() => {
          this.deleteDatabase().then(resolve).catch(reject);
        }, 1000);
      };
    });
  }
  
  // 🛡️ CLEANUP OLD DATA (when quota exceeded)
  async cleanupOldData() {
    logger.info('OfflineManager', 'Info');
    
    try {
      // Remove old analytics (keep last 7 days)
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const analytics = await this.db.getAll(STORES.ANALYTICS);
      
      let cleaned = 0;
      for (const item of analytics) {
        if (item.timestamp < sevenDaysAgo) {
          await this.db.delete(STORES.ANALYTICS, item.id);
          cleaned++;
        }
      }
      
      // Remove successfully synced items from sync queue
      const syncItems = await this.db.getAll(STORES.SYNC_QUEUE);
      for (const item of syncItems) {
        if (item.synced) {
          await this.db.delete(STORES.SYNC_QUEUE, item.id);
          cleaned++;
        }
      }
      
      logger.info('OfflineManager', 'Info');
      return cleaned;
      
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return 0;
    }
  }
  
  // Setup online/offline handlers
  setupOnlineOfflineHandlers() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      logger.info('OfflineManager', 'Info');
      this.syncOfflineData();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      logger.info('OfflineManager', 'Info');
    });
  }
  
  // Start periodic sync
  startPeriodicSync() {
    // Sync every 5 minutes when online
    setInterval(() => {
      if (this.isOnline && !this.syncInProgress) {
        this.syncOfflineData();
      }
    }, 5 * 60 * 1000);
  }
  
  // Save progress offline
  async saveProgress(progressData) {
    if (!this.db) return false;
    
    try {
      const tx = this.db.transaction(STORES.PROGRESS, 'readwrite');
      const store = tx.objectStore(STORES.PROGRESS);
      
      await store.put({
        id: 'current_progress',
        data: progressData,
        timestamp: Date.now()
      });
      
      logger.info('OfflineManager', 'Info');
      
      // Queue for sync if offline
      if (!this.isOnline) {
        await this.queueForSync('progress', progressData);
      }
      
      return true;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return false;
    }
  }
  
  // Get progress offline
  async getProgress() {
    if (!this.db) return null;
    
    try {
      const tx = this.db.transaction(STORES.PROGRESS, 'readonly');
      const store = tx.objectStore(STORES.PROGRESS);
      const result = await store.get('current_progress');
      
      return result ? result.data : null;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return null;
    }
  }
  
  // Cache lesson content
  async cacheLesson(lessonId, lessonData) {
    if (!this.db) return false;
    
    try {
      const tx = this.db.transaction(STORES.LESSONS, 'readwrite');
      const store = tx.objectStore(STORES.LESSONS);
      
      await store.put({
        id: lessonId,
        data: lessonData,
        timestamp: Date.now()
      });
      
      logger.info('OfflineManager', 'Info', lessonId);
      return true;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return false;
    }
  }
  
  // Get cached lesson
  async getCachedLesson(lessonId) {
    if (!this.db) return null;
    
    try {
      const tx = this.db.transaction(STORES.LESSONS, 'readonly');
      const store = tx.objectStore(STORES.LESSONS);
      const result = await store.get(lessonId);
      
      return result ? result.data : null;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return null;
    }
  }
  
  // Cache activity content
  async cacheActivity(activityId, activityData) {
    if (!this.db) return false;
    
    try {
      const tx = this.db.transaction(STORES.ACTIVITIES, 'readwrite');
      const store = tx.objectStore(STORES.ACTIVITIES);
      
      await store.put({
        id: activityId,
        data: activityData,
        timestamp: Date.now()
      });
      
      logger.info('OfflineManager', 'Info', activityId);
      return true;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return false;
    }
  }
  
  // Get cached activity
  async getCachedActivity(activityId) {
    if (!this.db) return null;
    
    try {
      const tx = this.db.transaction(STORES.ACTIVITIES, 'readonly');
      const store = tx.objectStore(STORES.ACTIVITIES);
      const result = await store.get(activityId);
      
      return result ? result.data : null;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return null;
    }
  }
  
  // Cache audio file
  async cacheAudio(audioUrl, audioBlob) {
    if (!this.db) return false;
    
    try {
      const tx = this.db.transaction(STORES.AUDIO, 'readwrite');
      const store = tx.objectStore(STORES.AUDIO);
      
      await store.put({
        url: audioUrl,
        blob: audioBlob,
        timestamp: Date.now()
      });
      
      logger.info('OfflineManager', 'Info', audioUrl);
      return true;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return false;
    }
  }
  
  // Get cached audio
  async getCachedAudio(audioUrl) {
    if (!this.db) return null;
    
    try {
      const tx = this.db.transaction(STORES.AUDIO, 'readonly');
      const store = tx.objectStore(STORES.AUDIO);
      const result = await store.get(audioUrl);
      
      return result ? result.blob : null;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return null;
    }
  }
  
  // Queue analytics for offline
  async queueAnalytics(analyticsData) {
    if (!this.db) return false;
    
    try {
      const tx = this.db.transaction(STORES.ANALYTICS, 'readwrite');
      const store = tx.objectStore(STORES.ANALYTICS);
      
      await store.add({
        data: analyticsData,
        timestamp: Date.now()
      });
      
      logger.info('OfflineManager', 'Info');
      return true;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return false;
    }
  }
  
  // Queue data for sync
  // 🛡️ ENHANCED: Queue for sync with priority and bounded size
  async queueForSync(type, data, priority = 'medium') {
    if (!this.db) return false;
    
    try {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      
      // Check current queue size
      const count = await store.count();
      
      if (count >= SYNC_CONFIG.MAX_QUEUE_SIZE) {
        logger.warn('OfflineManager', 'Warning');
        
        // For critical items, make room by removing low-priority items
        if (priority === 'critical' || priority === 'high') {
          await this.evictLowPriorityItems(store, 10); // Remove 10 low-priority items
        } else {
          logger.error('OfflineManager', 'Error');
          return false;
        }
      }
      
      // Map priority string to number
      const priorityValue = SYNC_CONFIG.PRIORITY[priority.toUpperCase()] || SYNC_CONFIG.PRIORITY.MEDIUM;
      
      const queueItem = {
        type: type,
        data: data,
        priority: priorityValue,
        priorityLabel: priority,
        timestamp: Date.now(),
        retryCount: 0,
        addedAt: new Date().toISOString()
      };
      
      await store.add(queueItem);
      
      logger.info('OfflineManager', 'Info');
      
      // If critical or high priority, trigger immediate sync if online
      if ((priority === 'critical' || priority === 'high') && this.isOnline && !this.syncInProgress) {
        logger.info('OfflineManager', 'Info');
        setTimeout(() => this.syncOfflineData(), 100);
      }
      
      return true;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return false;
    }
  }
  
  // Evict low-priority items to make room in queue
  async evictLowPriorityItems(store, count = 10) {
    try {
      // Get all items sorted by priority (descending) and timestamp (oldest first)
      const allItems = await store.getAll();
      
      // Find low-priority items (priority 3 or 4)
      const lowPriorityItems = allItems
        .filter(item => item.priority >= SYNC_CONFIG.PRIORITY.MEDIUM)
        .sort((a, b) => {
          // Sort by priority desc, then by timestamp asc (oldest first)
          if (a.priority !== b.priority) return b.priority - a.priority;
          return a.timestamp - b.timestamp;
        });
      
      // Remove oldest low-priority items
      const itemsToRemove = lowPriorityItems.slice(0, count);
      
      for (const item of itemsToRemove) {
        await store.delete(item.id);
        logger.info('OfflineManager', 'Info');
      }
      
      return itemsToRemove.length;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return 0;
    }
  }
  
  // Sync offline data
  async syncOfflineData() {
    if (!this.isOnline || this.syncInProgress || !this.db) {
      return;
    }
    
    this.syncInProgress = true;
    logger.info('OfflineManager', 'Info');
    
    try {
      // Sync analytics
      await this.syncAnalytics();
      
      // Sync queued data
      await this.syncQueuedData();
      
      logger.info('OfflineManager', 'Info');
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
    } finally {
      this.syncInProgress = false;
    }
  }
  
  // Sync analytics
  async syncAnalytics() {
    if (!this.db) return;
    
    try {
      const tx = this.db.transaction(STORES.ANALYTICS, 'readwrite');
      const store = tx.objectStore(STORES.ANALYTICS);
      const analytics = await store.getAll();
      
      for (const item of analytics) {
        try {
          // Send analytics data to server
          await fetch('/api/analytics', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(item.data)
          });
          
          // Remove from queue after successful send
          await store.delete(item.id);
        } catch (error) {
          logger.error('OfflineManager', 'Error', error);
        }
      }
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
    }
  }
  
  // 🛡️ ENHANCED: Sync queued data with priority processing and dead letter queue
  async syncQueuedData() {
    if (!this.db) return;
    
    try {
      const tx = this.db.transaction([STORES.SYNC_QUEUE, STORES.DEAD_LETTER], 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const deadLetterStore = tx.objectStore(STORES.DEAD_LETTER);
      const queuedItems = await store.getAll();
      
      // 🎯 Sort by priority first (1=critical, 2=high, etc.), then by timestamp
      queuedItems.sort((a, b) => {
        // Process critical items first
        if (a.priority !== b.priority) return a.priority - b.priority;
        // Within same priority, process oldest first
        return a.timestamp - b.timestamp;
      });
      
      logger.info('OfflineManager', 'Info');
      
      for (const item of queuedItems) {
        try {
          // Skip items in backoff period
          if (item.nextRetryTime && Date.now() < item.nextRetryTime) {
            logger.info('OfflineManager', 'Info');
            continue;
          }
          
          // Send data to server based on type
          let endpoint = '';
          let method = 'POST';
          
          switch (item.type) {
            case 'progress':
              endpoint = '/api/progress';
              method = 'PUT'; // Use PUT for progress updates
              break;
            case 'lesson_completion':
              endpoint = '/api/lessons/complete';
              break;
            case 'activity_completion':
              endpoint = '/api/activities/complete';
              break;
            case 'profile_update':
              endpoint = '/api/profiles/update';
              method = 'PUT';
              break;
            default:
              logger.warn('OfflineManager', 'Warning', item.type);
              continue;
          }
          
          const response = await fetch(endpoint, {
            method: method,
            headers: {
              'Content-Type': 'application/json',
              'X-Offline-Sync': 'true', // Flag to identify offline sync requests
              'X-Priority': item.priorityLabel || 'medium'
            },
            body: JSON.stringify({
              ...item.data,
              offlineTimestamp: item.timestamp,
              retryCount: item.retryCount || 0
            })
          });
          
          if (response.ok) {
            // Remove from queue after successful sync
            await store.delete(item.id);
            logger.info('OfflineManager', 'Info');
          } else if (response.status === 409) {
            // Conflict detected - handle conflict resolution
            await this.handleSyncConflict(item, response, store);
          } else {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        } catch (error) {
          logger.error('OfflineManager', 'Error', error);
          
          // Increment retry count
          item.retryCount = (item.retryCount || 0) + 1;
          item.lastError = error.message;
          item.lastRetryTime = Date.now();
          
          // Check if max retries exceeded
          if (item.retryCount > SYNC_CONFIG.MAX_RETRIES) {
            logger.warn('OfflineManager', 'Warning');
            
            // Move to dead letter queue for manual review/debugging
            await this.moveToDeadLetterQueue(item, deadLetterStore);
            await store.delete(item.id);
          } else {
            // Exponential backoff: wait longer between retries
            const backoffDelay = Math.min(
              SYNC_CONFIG.BACKOFF.INITIAL * Math.pow(SYNC_CONFIG.BACKOFF.MULTIPLIER, item.retryCount),
              SYNC_CONFIG.BACKOFF.MAX
            );
            item.nextRetryTime = Date.now() + backoffDelay;
            await store.put(item);
            
            logger.info('OfflineManager', 'Info');
          }
        }
      }
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
    }
  }
  
  // Move failed item to dead letter queue
  async moveToDeadLetterQueue(item, deadLetterStore) {
    try {
      const deadLetterItem = {
        ...item,
        movedToDeadLetterAt: Date.now(),
        movedToDeadLetterDate: new Date().toISOString(),
        finalError: item.lastError
      };
      
      await deadLetterStore.add(deadLetterItem);
      logger.info('OfflineManager', 'Info');
      
      // Enforce dead letter queue size limit
      await this.cleanupDeadLetterQueue(deadLetterStore);
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
    }
  }
  
  // Cleanup dead letter queue to maintain size limit
  async cleanupDeadLetterQueue(deadLetterStore) {
    try {
      const count = await deadLetterStore.count();
      
      if (count > SYNC_CONFIG.MAX_DEAD_LETTER_SIZE) {
        const allItems = await deadLetterStore.getAll();
        // Sort by timestamp (oldest first)
        allItems.sort((a, b) => a.movedToDeadLetterAt - b.movedToDeadLetterAt);
        
        // Remove oldest items to get back under limit
        const itemsToRemove = count - SYNC_CONFIG.MAX_DEAD_LETTER_SIZE;
        for (let i = 0; i < itemsToRemove; i++) {
          await deadLetterStore.delete(allItems[i].id);
        }
        
        logger.info('OfflineManager', 'Info');
      }
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
    }
  }
  
  // Handle sync conflicts
  async handleSyncConflict(item, response, store) {
    try {
      const conflictData = await response.json();
      logger.info('OfflineManager', 'Info', item.type);
      
      // For progress conflicts, merge the data
      if (item.type === 'progress') {
        const mergedData = this.mergeProgressData(item.data, conflictData.serverData);
        
        // Update the item with merged data
        item.data = mergedData;
        item.retryCount = (item.retryCount || 0) + 1;
        await store.put(item);
        
        logger.info('OfflineManager', 'Info');
      } else {
        // For other conflicts, use server data and remove from queue
        logger.info('OfflineManager', 'Info');
        await store.delete(item.id);
      }
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      // Remove the item to prevent infinite retries
      await store.delete(item.id);
    }
  }
  
  // Merge progress data to resolve conflicts
  mergeProgressData(localData, serverData) {
    const merged = { ...serverData };
    
    // Merge profiles
    if (localData.profiles && serverData.profiles) {
      merged.profiles = { ...serverData.profiles };
      
      for (const [profileId, localProfile] of Object.entries(localData.profiles)) {
        const serverProfile = serverData.profiles[profileId];
        
        if (serverProfile) {
          // Merge profile data, preferring newer timestamps
          merged.profiles[profileId] = {
            ...serverProfile,
            ...localProfile,
            // Keep the higher XP and streak values
            xp: Math.max(serverProfile.xp || 0, localProfile.xp || 0),
            streak: Math.max(serverProfile.streak || 0, localProfile.streak || 0),
            // Merge completed lessons and activities
            completedLessons: [...new Set([
              ...(serverProfile.completedLessons || []),
              ...(localProfile.completedLessons || [])
            ])],
            completedActivities: [...new Set([
              ...(serverProfile.completedActivities || []),
              ...(localProfile.completedActivities || [])
            ])],
            // Use the most recent lastActive timestamp
            lastActive: new Date(Math.max(
              new Date(serverProfile.lastActive || 0).getTime(),
              new Date(localProfile.lastActive || 0).getTime()
            )).toISOString()
          };
        } else {
          // Local profile doesn't exist on server, add it
          merged.profiles[profileId] = localProfile;
        }
      }
    }
    
    // Merge other progress data
    if (localData.subscriptionStatus && !serverData.subscriptionStatus) {
      merged.subscriptionStatus = localData.subscriptionStatus;
    }
    
    if (localData.onboardingComplete && !serverData.onboardingComplete) {
      merged.onboardingComplete = localData.onboardingComplete;
    }
    
    return merged;
  }
  
  // Get offline status
  getOfflineStatus() {
    return {
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress,
      hasDatabase: !!this.db
    };
  }
  
  // Get sync queue status
  async getSyncQueueStatus() {
    if (!this.db) return { totalItems: 0, failedItems: 0, pendingItems: 0 };
    
    try {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readonly');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const allItems = await store.getAll();
      
      const now = Date.now();
      const failedItems = allItems.filter(item => 
        item.retryCount > 0 && item.nextRetryTime && item.nextRetryTime > now
      );
      const pendingItems = allItems.filter(item => 
        item.retryCount === 0 || !item.nextRetryTime || item.nextRetryTime <= now
      );
      
      return {
        totalItems: allItems.length,
        failedItems: failedItems.length,
        pendingItems: pendingItems.length,
        items: allItems.map(item => ({
          id: item.id,
          type: item.type,
          timestamp: item.timestamp,
          retryCount: item.retryCount || 0,
          lastError: item.lastError,
          nextRetryTime: item.nextRetryTime
        }))
      };
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return { totalItems: 0, failedItems: 0, pendingItems: 0 };
    }
  }
  
  // Force sync with retry
  async forceSync() {
    if (this.syncInProgress) {
      logger.info('OfflineManager', 'Info');
      return false;
    }
    
    if (!this.isOnline) {
      logger.info('OfflineManager', 'Info');
      return false;
    }
    
    logger.info('OfflineManager', 'Info');
    await this.syncOfflineData();
    return true;
  }
  
  // Clear failed sync items
  async clearFailedSyncItems() {
    if (!this.db) return false;
    
    try {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const allItems = await store.getAll();
      
      const failedItems = allItems.filter(item => item.retryCount > 5);
      
      for (const item of failedItems) {
        await store.delete(item.id);
      }
      
      logger.info('OfflineManager', 'Info', failedItems.length, 'failed sync items');
      return true;
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return false;
    }
  }
  
  // Clear offline data
  async clearOfflineData() {
    if (!this.db) return;
    
    try {
      const stores = Object.values(STORES);
      
      for (const storeName of stores) {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        await store.clear();
      }
      
      logger.info('OfflineManager', 'Info');
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
    }
  }
  
  // Get storage usage
  async getStorageUsage() {
    if (!navigator.storage || !navigator.storage.estimate) {
      return null;
    }
    
    try {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage,
        available: estimate.quota,
        percentage: (estimate.usage / estimate.quota) * 100
      };
    } catch (error) {
      logger.error('OfflineManager', 'Error', error);
      return null;
    }
  }
}

// Create global instance
const offlineManager = new OfflineManager();

// Export for use in other modules
export default offlineManager;

// Export individual functions for convenience
export const {
  saveProgress,
  getProgress,
  cacheLesson,
  getCachedLesson,
  cacheActivity,
  getCachedActivity,
  cacheAudio,
  getCachedAudio,
  queueAnalytics,
  syncOfflineData,
  getOfflineStatus,
  getSyncQueueStatus,
  forceSync,
  clearFailedSyncItems,
  clearOfflineData,
  getStorageUsage
} = offlineManager;


